import { List } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { T } from "../../ui/tokens";
import DrivePreview from "./DrivePreview";
import ExtratoTabelaPreview from "./ExtratoTabelaPreview";
import type { MovimentoExtrato } from "./types";

interface Props {
  movimentos: MovimentoExtrato[];
  movimentoAtual: MovimentoExtrato;
  empresa: string;
}

const EXTENSOES_VISUALIZAVEIS = new Set(["pdf", "png", "jpg", "jpeg", "gif", "webp"]);

function podeMostrarOriginal(nome?: string | null): boolean {
  const extensao = (nome || "").split(".").pop()?.toLowerCase() || "";
  return EXTENSOES_VISUALIZAVEIS.has(extensao);
}

export default function ExtratoPreview({ movimentos, movimentoAtual, empresa }: Props) {
  const originalDisponivel = !!movimentoAtual.extrato_arquivo_link
    && podeMostrarOriginal(movimentoAtual.extrato_arquivo_nome);
  const vistaInicial = originalDisponivel ? "original" : "movimentos";
  const [vista, setVista] = useState<"original" | "movimentos">(vistaInicial);

  useEffect(() => {
    setVista(originalDisponivel ? "original" : "movimentos");
  }, [movimentoAtual.importacao_id, movimentoAtual.id, originalDisponivel]);

  const titulo = useMemo(
    () => movimentoAtual.extrato_arquivo_nome || "Extrato bancário",
    [movimentoAtual.extrato_arquivo_nome],
  );

  if (vista === "movimentos") {
    return (
      <ExtratoTabelaPreview
        movimentos={movimentos}
        movimentoAtual={movimentoAtual}
        onAbrirOriginal={originalDisponivel ? () => setVista("original") : undefined}
      />
    );
  }

  return (
    <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", background: T.surface }}>
      <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderBottom: `1px solid ${T.border}` }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: T.text }}>Extrato original</div>
          <div style={{ fontSize: 10.5, color: T.textLight, marginTop: 2 }}>{titulo}</div>
        </div>
        <button type="button" onClick={() => setVista("movimentos")} style={botao}>
          <List size={12} /> Ver movimentos
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DrivePreview driveLink={movimentoAtual.extrato_arquivo_link} empresa={empresa} titulo={titulo} />
      </div>
    </div>
  );
}

const botao: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 9px",
  border: `1px solid ${T.border}`, borderRadius: 7, background: T.surface,
  color: T.textMid, fontSize: 10.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
};
